import React, { useEffect, useRef, useState } from 'react';
import { array, arrayOf, bool, func, shape, string, oneOf, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

// Contexts
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
// Utils
import { FormattedMessage, intlShape, useIntl } from '../../util/reactIntl';
import { LISTING_STATE_PENDING_APPROVAL, LISTING_STATE_CLOSED, propTypes } from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
} from '../../util/urlHelpers';
import classNames from 'classnames';
import { convertMoneyToNumber } from '../../util/currency';
import {
  ensureListing,
  ensureOwnListing,
  ensureUser,
  userDisplayNameAsString,
} from '../../util/data';
import { richText } from '../../util/richText';
import {
  isBookingProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';

import { mainWarningParams } from '../../util/misc.js';

// Global ducks (for Redux actions and thunks)
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { managerForCategory } from '../../ducks/categoryAccountManagers.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';
import { initiateinquiryWithApplicationForm } from '../CheckoutPage/CheckoutPage.duck.js';
import { openSignInModal, SIGN_IN_INTENT_ENQUIRE } from '../../ducks/favoriteListings.duck';
import {
  PENDING_ACTION_ENQUIRE,
  takePendingListingAction,
} from '../../util/pendingListingAction';

// Shared components
import {
  H4,
  Page,
  NamedLink,
  NamedRedirect,
  OrderPanel,
  LayoutSingleColumn,
  Button,
  VerificationBadges,
  CompleteProfileModal,
  GetAccessModal,
  Bubble,
  WhiteBox,
  WarningBox,
  IconArrow,
  AccountManagerCard,
  ACCOUNT_MANAGER_CARD_LISTING,
  FavoriteListingButton,
  FAVORITE_BUTTON_LABELLED,
  ShareListingButton,
  SHARE_BUTTON_LABELLED,
} from '../../components';

import BuyerFormModal from './BuyerFormModal.js';
import SellerFormModal from './SellerFormModal.js';

// Related components and modules
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import {
  sendInquiry,
  setInitialValues,
  fetchTimeSlots,
  fetchTransactionLineItems,
  openListing,
  closeListing,
  checkUserTransactions,
} from './ListingPage.duck';

import {
  LoadingPage,
  ErrorPage,
  priceData,
  listingImages,
  handleContactUser,
  handleSubmitInquiry,
  handleSubmit,
} from './ListingPage.shared';
import ActionBarMaybe from './ActionBarMaybe';
import SectionTextMaybe from './SectionTextMaybe';
import SectionAuthorMaybe from './SectionAuthorMaybe';
import SectionMapMaybe from './SectionMapMaybe';
import CustomListingFields from './CustomListingFields';

import { ReactComponent as ClockIcon } from '../../assets/icons/clock.svg';
import { ReactComponent as AvatarIcon } from '../../assets/icons/defaultAvatar.svg';

import css from './ListingPage.module.css';
import { getMembershipId } from '../../util/dataExtractors.js';
import moment from 'moment';
// import { getUserPermissions } from '../../util/api.js';
// import SectionPassportRequired from './SectionPassportRequired.js';
import { pathByRouteName } from '../../util/routes.js';
import { useTrackedView } from '../../util/tracking.js';

const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;

const { UUID } = sdkTypes;

const USER_PERMISSION_ALLOW = 'permission/allow';

const getLastFiveDigits = (string) => {
  // Remove non-digit characters
  const digits = string.replace(/\D/g, '');
  return digits.slice(-5);
};

export const ListingPageComponent = props => {
  const [inquiryModalOpen, setInquiryModalOpen] = useState(
    props.inquiryModalOpenForListingId === props.params.id
  );

  const {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    intl,
    onManageDisableScrolling,
    params: rawParams,
    location,
    scrollingDisabled,
    showListingError,
    reviews,
    fetchReviewsError,
    sendInquiryInProgress,
    sendInquiryError,
    monthlyTimeSlots,
    onFetchTimeSlots,
    onFetchTransactionLineItems,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    history,
    callSetInitialValues,
    onSendInquiry,
    onInitializeCardPaymentData,
    config,
    routeConfiguration,
    currentUserPermissions,
    openingListing,
    closingListing,
    closeListing,
    openListing,
    updatedListing,
    checkUserTransactions,
    userHasViewAccess,
    ongoingTransaction,
    closedTransaction,
    initiateInquiryInProgress,
    initiateInquiryError,
    onInitiateinquiryWithApplicationForm,
    managersByCategory,
    onOpenSignInPrompt,
  } = props;

  // const [authorPermissions, setAuthorPermissions] = useState(null);
  // const [fetchingUserPermissions, setFetchingUserPermissions] = useState(false);
  const [showCompleteProfile, setShowCompleteProfile] = useState(false);
  const [showGetAccessModal, setShowGetAccessModal] = useState(false);
  const [showBuyerForm, setShowBuyerForm] = useState(false);
  const [showSellerForm, setShowSellerForm] = useState(false);

  const listingConfig = config.listing;
  const listingId = new UUID(rawParams.id);
  const isPendingApprovalVariant = rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;

  //  const { userFields } = config?.user;
  //   const { userPuplicData } = currentUser?.attributes?.profile?.publicData;

  //     const userfieldkeys = userFields.map(field => field.key)

  //     const missingFields = userfieldkeys.filter(key => {
  //         // if (!publicData?.[key]) {
  //         //     console.log('Missing field for key:', key);
  //         // }
  //         return !userPuplicData?.[key];
  //     });

  const currentListing =
    isPendingApprovalVariant || isDraftVariant
      ? ensureOwnListing(getOwnListing(listingId))
      : ensureListing(getListing(listingId));

  const listingUuid = currentListing?.id?.uuid;
  const currentUserUuid = currentUser?.id?.uuid;

  // Which listing and member the existing-chat check last finished for. The
  // resumed enquiry below waits on it: until it lands we cannot tell whether
  // Enquire should open the application form or go to a chat that already
  // exists.
  const [transactionsCheckedFor, setTransactionsCheckedFor] = useState(null);

  useEffect(() => {
    if (listingUuid && currentUserUuid) {
      checkUserTransactions({
        listingId: listingUuid,
        userId: currentUserUuid,
      }).then(() => setTransactionsCheckedFor(`${listingUuid}:${currentUserUuid}`));
    }
    // The listing and the current user are fetched asynchronously, so this has
    // to re-run once both of them are available.
  }, [listingUuid, currentUserUuid]);

  // A visitor who pressed Enquire while signed out was sent to sign in, and has
  // come back here. Carry on as if they had just pressed it: the application
  // form, the complete-profile prompt, or their existing chat.
  //
  // handleConditionalSubmit is defined further down, after the early returns
  // for a listing that is still loading, so it cannot be called from here
  // directly. The ref is set on every full render; this effect only runs once
  // the listing and the member are both loaded, which means that render has
  // happened.
  const resumeEnquiryRef = useRef(null);
  useEffect(() => {
    const isChecked =
      !!listingUuid &&
      !!currentUserUuid &&
      transactionsCheckedFor === `${listingUuid}:${currentUserUuid}`;
    if (!isChecked) {
      return;
    }
    if (takePendingListingAction(PENDING_ACTION_ENQUIRE, listingUuid) && resumeEnquiryRef.current) {
      resumeEnquiryRef.current({});
    }
  }, [transactionsCheckedFor, listingUuid, currentUserUuid]);

  // Listing view + dwell time for the CRM (logged-in members only).
  useTrackedView({
    eventType: 'listingView',
    data: {
      listingId: currentListing?.id?.uuid,
      source: 'listingPage',
    },
    subjectKey: currentListing?.id?.uuid,
    enabled: !!(currentListing?.id?.uuid && currentUser?.id?.uuid),
  });


  // useEffect(() => {
  //   const fetchUserPermissions = async _currentUser => {
  //     try {
  //       setFetchingUserPermissions(true);
  //       const response = await getUserPermissions(_currentUser.id.uuid);
  //       if (response?.data) {
  //         setAuthorPermissions(response.data);
  //       }
  //     } catch (error) {
  //       console.error('fetchUserPermissions', error);
  //     } finally {
  //       setFetchingUserPermissions(false);
  //     }
  //   };

  //   // Check if we need to fetch permissions
  //   if (currentListing.author && !fetchingUserPermissions && !authorPermissions) {
  //     fetchUserPermissions(currentListing.author);
  //   }
  // }, [currentListing.author, fetchingUserPermissions, authorPermissions]); // Ensure correct dependencies

  if (updatedListing) {
    currentListing.attributes = updatedListing.attributes;
  }

  const listingSlug = rawParams.slug || createSlug(currentListing.attributes.title || '');
  const params = { slug: listingSlug, ...rawParams };

  const listingPathParamType = isDraftVariant
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;
  const listingTab = isDraftVariant ? 'photos' : 'details';

  const isApproved =
    currentListing.id && currentListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

  const pendingIsApproved = isPendingApprovalVariant && isApproved;
  // If a /pending-approval URL is shared, the UI requires
  // authentication and attempts to fetch the listing from own
  // listings. This will fail with 403 Forbidden if the author is
  // another user. We use this information to try to fetch the
  // public listing.
  const pendingOtherUsersListing =
    (isPendingApprovalVariant || isDraftVariant) &&
    showListingError &&
    showListingError.status === 403;
  const shouldShowPublicListingPage = pendingIsApproved || pendingOtherUsersListing;

  if (shouldShowPublicListingPage) {
    return <NamedRedirect name="ListingPage" params={params} search={location.search} />;
  }

  const topbar = <TopbarContainer />;

  if (showListingError && showListingError.status === 404) {
    // 404 listing not found
    return <NotFoundPage />;
  } else if (showListingError) {
    // Other error in fetching listing
    return <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  } else if (!currentListing.id) {
    // Still loading the listing
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  }

  const {
    description = '',
    geolocation = null,
    price = null,
    title = '',
    publicData = {},
    metadata = {},
    createdAt,
  } = currentListing.attributes;

  const listingType = publicData?.listingType;
  const isSale = listingType === 'sell';
  const isSearch = listingType === 'search';

  const richTitle = (
    <span>
      {richText(title, {
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
        longWordClass: css.longWord,
      })}
    </span>
  );

  const authorAvailable = currentListing && currentListing.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
  const isOwnListing =
    userAndListingAuthorAvailable && currentListing.author.id.uuid === currentUser.id.uuid;

  const transactionProcessAlias = publicData?.transactionProcessAlias;
  const transactionProcessAliasFirstPart = transactionProcessAlias && transactionProcessAlias.split('/')[0];
  const processName = resolveLatestProcessName(transactionProcessAliasFirstPart);
  const isBooking = isBookingProcess(processName);
  const isPurchase = isPurchaseProcess(processName);
  const processType = isBooking ? ('booking' ? isPurchase : 'purchase') : 'inquiry';

  const currentAuthor = authorAvailable ? currentListing.author : null;
  const ensuredAuthor = ensureUser(currentAuthor);
  const noPayoutDetailsSetWithOwnListing =
    isOwnListing && (processType !== 'inquiry' && !currentUser?.attributes?.stripeConnected);
  const payoutDetailsWarning = noPayoutDetailsSetWithOwnListing ? (
    <span className={css.payoutDetailsWarning}>
      <FormattedMessage id="ListingPage.payoutDetailsWarning" values={{ processType }} />
      <NamedLink name="StripePayoutPage">
        <FormattedMessage id="ListingPage.payoutDetailsWarningLink" />
      </NamedLink>
    </span>
  ) : null;

  // When user is banned or deleted the listing is also deleted.
  // Because listing can be never showed with banned or deleted user we don't have to provide
  // banned or deleted display names for the function

  // const authorDisplayName = getMembershipId(ensuredAuthor, '');
  const isUserVerified = !!(currentAuthor?.attributes?.profile?.publicData?.isVerified
  );
  const authorDisplayName = null;

  const { formattedPrice } = priceData(price, config.currency, intl);

  const commonParams = { params, history, routes: routeConfiguration };
  const onContactUser = handleContactUser({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    location,
    setInitialValues,
    setInquiryModalOpen,
  });

  const handlePassportRequired = () => {
    history.push(pathByRouteName('PassportRequiredPage', routeConfiguration));
  };

  // Note: this is for inquiry state in booking and purchase processes. Inquiry process is handled through handleSubmit.
  const onSubmitInquiry = handleSubmitInquiry({
    ...commonParams,
    getListing,
    onSendInquiry,
    setInquiryModalOpen,
  });

  const onSubmit = handleSubmit({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    getListing,
    onInitializeCardPaymentData,
  });

  const handleOrderSubmit = values => {
    const isCurrentlyClosed = currentListing.attributes.state === LISTING_STATE_CLOSED;
    if (isOwnListing || isCurrentlyClosed) {
      window.scrollTo(0, 0);
    } else {
      onSubmit(values);
    }
  };
  // A chat is a transaction. Which page shows it depends on the viewer's role:
  // the customer (who applied) sees the order page, the provider the sale page.
  const chatPathFor = transaction =>
    transaction
      ? pathByRouteName(
        transaction.role === 'provider' ? 'SaleDetailsPage' : 'OrderDetailsPage',
        routeConfiguration,
        { id: transaction.id }
      )
      : null;

  // The most recent chat that is still open. When there is one, the user should
  // continue it instead of sending another application form.
  const ongoingChatPath = chatPathFor(ongoingTransaction);
  // Only relevant when there is no open chat: the user had a chat here before,
  // but it was closed. They may still send a new form.
  const closedChatPath = ongoingChatPath ? null : chatPathFor(closedTransaction);

  const handleConditionalSubmit = values => {
    const userMetadata = currentUser?.attributes?.profile?.metadata || {};

    const isPremium = userMetadata.isPremium;
    const isSaleListing = currentListing?.attributes?.publicData?.listingType === 'sell'

    // Signed out: ask them to sign in first. Signing in brings them back here
    // with the enquiry resumed (see resumeEnquiryRef above); "Apply for
    // membership" goes to the contact form, where this used to go directly.
    if (!currentUser) {
      onOpenSignInPrompt(currentListing.id.uuid);
      return;
    };

    if (isOwnListing) {
      return
    }

    if (ongoingChatPath) {
      return history.push(ongoingChatPath);
    }

    if (!!currentUser && !currentUser?.attributes?.profile?.publicData?.profileUpdated) {
      setShowCompleteProfile(true);
      return;
    };

    if (isSaleListing) {
      setShowBuyerForm(true)
    } else {
      setShowSellerForm(true);
    };

    // const allowedToTransact = currentUserPermissions?.initiateTransactions === USER_PERMISSION_ALLOW;

    // if (allowedToTransact || !currentUser) {
    // handleOrderSubmit(values);
    // } else {
    //   handlePassportRequired();
    // }
  };
  resumeEnquiryRef.current = handleConditionalSubmit;


  const facebookImages = listingImages(currentListing, 'facebook');
  const twitterImages = listingImages(currentListing, 'twitter');
  const schemaImages = listingImages(
    currentListing,
    `${config.layout.listingImage.variantPrefix}-2x`
  ).map(img => img.url);
  const marketplaceName = config.marketplaceName;
  const schemaTitle = intl.formatMessage(
    { id: 'ListingPage.schemaTitle' },
    { title, price: formattedPrice, marketplaceName }
  );
  // You could add reviews, sku, etc. into page schema
  // Read more about product schema
  // https://developers.google.com/search/docs/advanced/structured-data/product
  const productURL = `${config.marketplaceRootURL}${location.pathname}${location.search}${location.hash}`;
  const schemaPriceMaybe = price
    ? {
      price: intl.formatNumber(convertMoneyToNumber(price), {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
      priceCurrency: price.currency,
    }
    : {};
  const currentStock = currentListing.currentStock?.attributes?.quantity || 0;
  const schemaAvailability =
    currentStock > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock';

  const scrambleWords = (input, opts = {}) => {
    const rng = (opts.seed === undefined) ? Math.random : mulberry32(opts.seed);
    const letters = "abcdefghijklmnopqrstuvwxyz";
    return input.replace(/\S/gu, ch => {
      const rand = letters[Math.floor(rng() * 26)];
      return ch === ch.toUpperCase() ? rand.toUpperCase() : rand;
    });
  }

  const budgetToTake = isSearch
    ? publicData.Budget_Maximum
    : publicData.asking_price;
  const formattedBudget = budgetToTake
    ? `${budgetToTake.toLocaleString()} ${publicData.currency}`
    : "Undisclosed";

  // const memberProfile = isSearch
  //   ? publicData.buyerProfile
  //   : publicData.sellingPrincipalProfile;

  // const memberProfileConfig = isSearch
  //   ? listingConfig.listingFields.find(i => i.key == "buyerProfile")
  //   : listingConfig.listingFields.find(i => i.key == "sellingPrincipalProfile");

  // const memberProfileOptions = memberProfileConfig?.enumOptions || [];
  // const memberProfileLabel = memberProfileOptions.find(o => o.option == memberProfile)?.label;

  const listingCreatorProfile = publicData.listingCreatorProfile;
  const listingCreatorProfileConfig = listingConfig.listingFields.find(i => i.key == "listingCreatorProfile");

  const listingCreatorProfileOptions = listingCreatorProfileConfig?.enumOptions || [];
  const listingCreatorProfileLabel = listingCreatorProfileOptions.find(o => o.option == listingCreatorProfile)?.label;

  const categories = config?.categoryConfiguration?.categories || [];
  const categoryLabel = categories.find(c => c.id == publicData.categoryLevel1)?.name;

  const searchString = location?.state?.searchString;

  const orderPanel = <OrderPanel
    className={css.productOrderPanel}
    listing={currentListing}
    isOwnListing={isOwnListing}
    onSubmit={handleConditionalSubmit}
    ongoingChatPath={ongoingChatPath}
    closedChatPath={closedChatPath}
    title={<FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />}
    payoutDetailsWarning={payoutDetailsWarning}
    author={ensuredAuthor}
    // authorPermissions={authorPermissions}
    onManageDisableScrolling={onManageDisableScrolling}
    onContactUser={onContactUser}
    monthlyTimeSlots={monthlyTimeSlots}
    onFetchTimeSlots={onFetchTimeSlots}
    onFetchTransactionLineItems={onFetchTransactionLineItems}
    lineItems={lineItems}
    fetchLineItemsInProgress={fetchLineItemsInProgress}
    fetchLineItemsError={fetchLineItemsError}
    validListingTypes={config.listing.listingTypes}
    marketplaceCurrency={config.currency}
    dayCountAvailableForBooking={config.stripe.dayCountAvailableForBooking}
    marketplaceName={config.marketplaceName}
    headlinePriceLabel={intl.formatMessage({
      id: isSearch ? 'ListingPage.budgetLabel' : 'ListingPage.askingPriceLabel',
    })}
    headlinePrice={formattedBudget}
  />;

  const lastOnline = ensuredAuthor?.attributes?.profile?.publicData?.lastOnline;
  const memCode = getLastFiveDigits(rawParams.id);

  // The manager who owns this listing's desk.
  //
  // This is derived from the LISTING's own category, not from whatever the
  // reader had filtered by before they arrived here — the card names the person
  // who holds this listing's proof documents, so it has to follow the listing.
  //
  // Null is normal: only some categories have a desk, and the card is then left
  // out rather than replaced by a placeholder.
  const listingManager = managerForCategory(managersByCategory, publicData.categoryLevel1);

  // memCode is the listing reference the page already shows under the creator,
  // so the subject line and the page agree on how this listing is named.
  const accountManagerCard = listingManager ? (
    <AccountManagerCard
      manager={listingManager}
      variant={ACCOUNT_MANAGER_CARD_LISTING}
      eyebrow={intl.formatMessage({ id: 'AccountManagerCard.listingEyebrow' })}
      mailtoSubject={intl.formatMessage(
        { id: 'AccountManagerCard.listingSubject' },
        { listingRef: memCode }
      )}
      whatsappText={intl.formatMessage(
        { id: 'AccountManagerCard.listingWhatsappText' },
        { firstName: listingManager.firstName, listingRef: memCode }
      )}
      onManageDisableScrolling={onManageDisableScrolling}
    />
  ) : null;

  // Who listed it, under the title and date on every width. It used to head the
  // Enquire box in the rail; the rail now leads with the price instead.
  //
  // "a" or "an" follows the label, since the labels come from the hosted
  // listing config and may start with a vowel ("an Advisor").
  const creatorTitle = listingCreatorProfileLabel
    ? intl.formatMessage(
      {
        id: /^[aeiou]/i.test(listingCreatorProfileLabel)
          ? 'ListingPage.listedByAn'
          : 'ListingPage.listedByA',
      },
      { profile: listingCreatorProfileLabel }
    )
    : intl.formatMessage({ id: 'ListingPage.listingCreator' });

  const creatorRow = (
    <div className={css.creatorRow}>
      <div className={css.creatorAvatar}>
        <AvatarIcon />
      </div>
      <div className={css.creatorText}>
        <div className={css.creatorTitle}>{creatorTitle}</div>
        <div className={css.verificationBadges}>
          <VerificationBadges user={ensuredAuthor} />
        </div>
        <div className={css.memCode}>Code: #{memCode}</div>
      </div>
    </div>
  );

  // Share and Save. On the title row on desktop, on the Back row on a phone.
  const listingActions = (
    <>
      <ShareListingButton listing={currentListing} variant={SHARE_BUTTON_LABELLED} />
      <FavoriteListingButton listingId={listingUuid} variant={FAVORITE_BUTTON_LABELLED} />
    </>
  );

  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      author={authorDisplayName}
      description={description}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'Product',
        description: description,
        name: schemaTitle,
        image: schemaImages,
        offers: {
          '@type': 'Offer',
          url: productURL,
          ...schemaPriceMaybe,
          availability: schemaAvailability,
        },
      }}
    >
      <LayoutSingleColumn className={css.pageRoot} topbar={topbar} footer={<FooterContainer />}>
        <div className={css.backRow}>
          <NamedLink
            name="SearchPage"
            to={searchString ? { search: searchString } : {}}
            className={css.returnButton}
          >
            <IconArrow direction="left" />
            <FormattedMessage id="ListingPageCarousel.backButton" />
          </NamedLink>
          <div className={css.listingActionsMobile}>{listingActions}</div>
        </div>

        <div className={css.contentWrapperForProductLayout}>
          <div className={css.mainColumnForProductLayout}>
            {currentListing.id && noPayoutDetailsSetWithOwnListing ? (
              <ActionBarMaybe
                className={css.actionBarForProductLayout}
                isOwnListing={isOwnListing}
                listing={currentListing}
                showNoPayoutDetailsSet={noPayoutDetailsSetWithOwnListing}
                onCloseListing={closeListing}
                onOpenListing={openListing}
                actionsInProgressListingId={openingListing || closingListing}
              />
            ) : null}
            {currentListing.id ? (
              <ActionBarMaybe
                className={css.actionBarForProductLayout}
                isOwnListing={isOwnListing}
                listing={currentListing}
                editParams={{
                  id: listingId.uuid,
                  slug: listingSlug,
                  type: listingPathParamType,
                  tab: listingTab,
                }}
                onCloseListing={closeListing}
                onOpenListing={openListing}
                actionsInProgressListingId={openingListing || closingListing}
              />
            ) : null}

            {/* On a phone only the panel's fixed bar at the bottom of the
                screen shows: price, Enquire, Debt Advisory. */}
            <div className={css.mobileOnly}>
              {orderPanel}
            </div>
            {/* <SectionGallery
              listing={currentListing}
              variantPrefix={config.layout.listingImage.variantPrefix}
            /> */}
            <div className={css.topHeader}>
              <Bubble type={listingType} />
              <div className={css.infoBubble}>
                {categoryLabel}
              </div>
              <div className={css.listingActionsDesktop}>{listingActions}</div>
            </div>

            <H4 as="h1" className={css.title}>
              {title}
            </H4>

            <div className={css.orderPanelListingCreatedDate}>
              Created: {moment(createdAt).format('DD/MM/YYYY')}
            </div>

            {creatorRow}

            <WhiteBox className={css.mainWhiteBox}>
              <CustomListingFields
                publicData={publicData}
                metadata={metadata}
                listingFieldConfigs={listingConfig.listingFields}
                categoryConfiguration={config.categoryConfiguration}
                intl={intl}
                currentUser={currentUser}
                scrambleWords={scrambleWords}
              />

              <h5 className={css.descLabel}>
                {isSearch ? "Description" : "Teaser Description"}
              </h5>
              <div className={css.descText}>{description}</div>
            </WhiteBox>

            {accountManagerCard ? (
              <div className={css.mobileOnly}>{accountManagerCard}</div>
            ) : null}

            <WarningBox
              title={mainWarningParams.title}
              content={mainWarningParams.content}
              className={css.listingWarning}
            />

            {/* <SectionMapMaybe
              geolocation={geolocation}
              publicData={publicData}
              listingId={currentListing.id}
              mapsConfig={config.maps}
            /> */}
            {/* <SectionReviews reviews={reviews} fetchReviewsError={fetchReviewsError} /> */}
            {/* <SectionAuthorMaybe
              title={title}
              listing={currentListing}
              currentAuthor={currentAuthor}
              // authorPermissions={authorPermissions}
              authorDisplayName={authorDisplayName}
              onContactUser={onContactUser}
              isInquiryModalOpen={isAuthenticated && inquiryModalOpen}
              onCloseInquiryModal={() => setInquiryModalOpen(false)}
              sendInquiryError={sendInquiryError}
              sendInquiryInProgress={sendInquiryInProgress}
              onSubmitInquiry={onSubmitInquiry}
              currentUser={currentUser}
              currentUserPermissions={currentUserPermissions}
              onManageDisableScrolling={onManageDisableScrolling}
            /> */}
          </div>
          <div className={css.orderColumnForProductLayout}>
            <div className={css.desktopOnly}>
              {/* The price, then Enquire and Debt Advisory. */}
              <WhiteBox className={css.rhcBox}>{orderPanel}</WhiteBox>
              {/* Its own card in the rail, directly under the Enquire and Debt
                  Advisory buttons, rather than another row inside the creator
                  box. */}
              {accountManagerCard}
            </div>

            {showCompleteProfile ? <CompleteProfileModal
              onManageDisableScrolling={onManageDisableScrolling}
              variant="Inquiry"
            /> : null}

            {showGetAccessModal && <GetAccessModal
              onManageDisableScrolling={onManageDisableScrolling}
              onClose={() => setShowGetAccessModal(false)}
              isOpen={showGetAccessModal} />}

            {showBuyerForm && <BuyerFormModal
              onManageDisableScrolling={onManageDisableScrolling}
              onClose={() => setShowBuyerForm(false)}
              isOpen={showBuyerForm}
              processName={processName}
              listing={currentListing}
              currentUser={currentUser}
              config={config}
              initiateInquiryInProgress={initiateInquiryInProgress}
              initiateInquiryError={initiateInquiryError}
              onInitiateinquiryWithApplicationForm={onInitiateinquiryWithApplicationForm}
            />}

            {showSellerForm && <SellerFormModal
              onManageDisableScrolling={onManageDisableScrolling}
              onClose={() => setShowSellerForm(false)}
              isOpen={showSellerForm}
              processName={processName}
              listing={currentListing}
              currentUser={currentUser}
              config={config}
              initiateInquiryInProgress={initiateInquiryInProgress}
              initiateInquiryError={initiateInquiryError}
              onInitiateinquiryWithApplicationForm={onInitiateinquiryWithApplicationForm}
            />}

          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

ListingPageComponent.defaultProps = {
  currentUser: null,
  inquiryModalOpenForListingId: null,
  showListingError: null,
  reviews: [],
  fetchReviewsError: null,
  monthlyTimeSlots: null,
  sendInquiryError: null,
  lineItems: null,
  fetchLineItemsError: null,
};

ListingPageComponent.propTypes = {
  // from useHistory
  history: shape({
    push: func.isRequired,
  }).isRequired,
  // from useLocation
  location: shape({
    search: string,
  }).isRequired,

  // from useIntl
  intl: intlShape.isRequired,

  // from useConfiguration
  config: object.isRequired,
  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,

  params: shape({
    id: string.isRequired,
    slug: string,
    variant: oneOf([LISTING_PAGE_DRAFT_VARIANT, LISTING_PAGE_PENDING_APPROVAL_VARIANT]),
  }).isRequired,

  isAuthenticated: bool.isRequired,
  currentUser: propTypes.currentUser,
  getListing: func.isRequired,
  getOwnListing: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  scrollingDisabled: bool.isRequired,
  inquiryModalOpenForListingId: string,
  showListingError: propTypes.error,
  callSetInitialValues: func.isRequired,
  reviews: arrayOf(propTypes.review),
  fetchReviewsError: propTypes.error,
  monthlyTimeSlots: object,
  // monthlyTimeSlots could be something like:
  // monthlyTimeSlots: {
  //   '2019-11': {
  //     timeSlots: [],
  //     fetchTimeSlotsInProgress: false,
  //     fetchTimeSlotsError: null,
  //   }
  // }
  sendInquiryInProgress: bool.isRequired,
  sendInquiryError: propTypes.error,
  onSendInquiry: func.isRequired,
  onInitializeCardPaymentData: func.isRequired,
  onFetchTransactionLineItems: func.isRequired,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,
  closingListing: shape({ uuid: string.isRequired }),
  openingListing: shape({ uuid: string.isRequired }),
  // Opens the sign-in prompt in front of Enquire for a signed-out visitor.
  onOpenSignInPrompt: func.isRequired,
};

const EnhancedListingPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  return (
    <ListingPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  const {
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    sendInquiryInProgress,
    sendInquiryError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    inquiryModalOpenForListingId,
    updatedListing,
    openingListing,
    openingListingError,
    closingListing,
    closingListingError,
    userHasViewAccess,
    ongoingTransaction,
    closedTransaction,
  } = state.ListingPage;
  const { currentUser } = state.user;
  const { managersByCategory } = state.categoryAccountManagers;
  const {
    initiateInquiryInProgress,
    initiateInquiryError,
  } = state.CheckoutPage;

  const currentUserPermissions = currentUser?.attributes?.permissions;

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  return {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    inquiryModalOpenForListingId,
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    sendInquiryInProgress,
    sendInquiryError,
    currentUserPermissions,
    openingListing,
    openingListingError,
    closingListing,
    closingListingError,
    updatedListing,
    userHasViewAccess,
    ongoingTransaction,
    closedTransaction,
    initiateInquiryInProgress,
    initiateInquiryError,
    managersByCategory,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onFetchTransactionLineItems: params => dispatch(fetchTransactionLineItems(params)),
  onSendInquiry: (listing, message) => dispatch(sendInquiry(listing, message)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onFetchTimeSlots: (listingId, start, end, timeZone) =>
    dispatch(fetchTimeSlots(listingId, start, end, timeZone)),
  closeListing: listingId => dispatch(closeListing(listingId)),
  openListing: listingId => dispatch(openListing(listingId)),
  checkUserTransactions: params => dispatch(checkUserTransactions(params)),
  onInitiateinquiryWithApplicationForm: (inquiryParams, transactionProcessAlias, transition) =>
    dispatch(initiateinquiryWithApplicationForm(inquiryParams, transactionProcessAlias, transition)),
  onOpenSignInPrompt: listingId =>
    dispatch(openSignInModal({ intent: SIGN_IN_INTENT_ENQUIRE, listingId })),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const ListingPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(EnhancedListingPage);

export default ListingPage;
